/**
 * Boot-time configuration loader for the Portal SPA.
 *
 * The exact same SPA bundle serves two runtime modes distinguished at boot:
 *
 *   - `{ mode: "authoring",  apiBase: "/api" }`
 *   - `{ mode: "published",  snapshotUrl: "portfolio.json.gz", publishedAt: "…" }`
 *
 * The narrowing performed here is where the read-only guarantee is grounded
 * (Requirement 17): every UI feature is gated by the resulting `Config.mode`,
 * so a `published` boot never wires up mutation affordances.
 *
 * The load is a one-shot per page: results are cached in a module-scoped
 * promise so repeated `loadConfig()` calls do not re-fetch. Tests use
 * `__resetConfigCacheForTests()` to clear the cache between cases.
 */
import type { Config } from "@aim/types";

/** Error raised by `loadConfig` when the boot config cannot be resolved. */
export class ConfigLoadError extends Error {
  readonly code: "FETCH_FAILED" | "PARSE_FAILED" | "INVALID_SHAPE";

  constructor(
    code: "FETCH_FAILED" | "PARSE_FAILED" | "INVALID_SHAPE",
    message: string,
  ) {
    super(message);
    this.name = "ConfigLoadError";
    this.code = code;
    // Restore prototype chain across the `extends Error` boundary so
    // `instanceof ConfigLoadError` works after TS downlevels to ES2022+.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Type guard narrowing an arbitrary parsed JSON value to `Config`.
 *
 * Mirrors the discriminated union in `@aim/types`:
 *   - authoring   → { mode: "authoring",  apiBase: string }
 *   - published   → { mode: "published",  snapshotUrl: string, publishedAt: string }
 */
function isConfig(value: unknown): value is Config {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const v = value as Record<string, unknown>;
  if (v.mode === "authoring") {
    return typeof v.apiBase === "string";
  }
  if (v.mode === "published") {
    return (
      typeof v.snapshotUrl === "string" && typeof v.publishedAt === "string"
    );
  }
  return false;
}

/** Module-scoped cache. `null` means "not yet requested (or reset)". */
let cachedConfigPromise: Promise<Config> | null = null;

/**
 * Fetch `/config.json` and narrow to the authoring vs published union.
 *
 * Boot-time single-shot: the first call caches its Promise for the lifetime
 * of the page. Subsequent calls (including concurrent ones during boot)
 * return the cached promise.
 *
 * @param fetchImpl Optional `fetch` implementation for tests. Defaults to
 *   `globalThis.fetch`.
 * @throws {ConfigLoadError} `"FETCH_FAILED"` on network failure or non-2xx.
 * @throws {ConfigLoadError} `"PARSE_FAILED"` on invalid JSON.
 * @throws {ConfigLoadError} `"INVALID_SHAPE"` when the payload does not
 *   satisfy the authoring/published discriminated union.
 */
export async function loadConfig(
  fetchImpl?: typeof fetch,
): Promise<Config> {
  if (cachedConfigPromise !== null) {
    return cachedConfigPromise;
  }

  const doFetch = fetchImpl ?? globalThis.fetch;
  if (typeof doFetch !== "function") {
    // Surface an environment problem the caller can act on rather than
    // silently defaulting to authoring mode.
    throw new ConfigLoadError(
      "FETCH_FAILED",
      "No fetch implementation available in this environment.",
    );
  }

  const promise = (async (): Promise<Config> => {
    let response: Response;
    try {
      // Use a relative URL so the SPA boots the same way regardless of
      // whether it's served from the dev server, a static host at a
      // sub-path (e.g. GitHub Pages `<user>.github.io/<repo>/`), or a
      // local file:// origin. The Vite dev proxy still catches the
      // request because it matches on path suffix.
      response = await doFetch("config.json");
    } catch (cause) {
      throw new ConfigLoadError(
        "FETCH_FAILED",
        `Failed to fetch /config.json: ${describeError(cause)}`,
      );
    }

    if (!response.ok) {
      throw new ConfigLoadError(
        "FETCH_FAILED",
        `Failed to fetch /config.json: HTTP ${String(response.status)}`,
      );
    }

    let body: string;
    try {
      body = await response.text();
    } catch (cause) {
      throw new ConfigLoadError(
        "FETCH_FAILED",
        `Failed to read /config.json response body: ${describeError(cause)}`,
      );
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(body);
    } catch (cause) {
      throw new ConfigLoadError(
        "PARSE_FAILED",
        `/config.json is not valid JSON: ${describeError(cause)}`,
      );
    }

    if (!isConfig(parsed)) {
      throw new ConfigLoadError(
        "INVALID_SHAPE",
        "/config.json does not match the authoring/published Config shape.",
      );
    }

    return parsed;
  })();

  cachedConfigPromise = promise;

  // If the load throws, evict the failed promise so a subsequent boot attempt
  // can retry rather than being stuck with a rejected cache entry.
  promise.catch(() => {
    if (cachedConfigPromise === promise) {
      cachedConfigPromise = null;
    }
  });

  return promise;
}

function describeError(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message;
  }
  return String(cause);
}

/**
 * Test-only cache reset. Not exported through `@aim/web`'s public surface;
 * used by co-located tests to guarantee a clean cache per case.
 */
export function __resetConfigCacheForTests(): void {
  cachedConfigPromise = null;
}
